import type { JSX } from 'react';
import { allClasses, findClass } from '@bratva/content';
import { CLASS_LEVEL, SUBCLASS_LEVEL, xpProgress } from '@bratva/rules';
import type { SaveDoc } from '@bratva/state';
import { character } from '../game/character.js';
import { store } from '../game/store.js';

interface Props {
  save: SaveDoc;
  onClose(): void;
}

type Spendable = 'sila' | 'smekalochka' | 'obayanie' | 'chotkost';

const STATS: { key: Spendable; title: string }[] = [
  { key: 'sila', title: 'Сила' },
  { key: 'smekalochka', title: 'Смекалочка' },
  { key: 'obayanie', title: 'Обаяние' },
  { key: 'chotkost', title: 'Чоткость' },
];

/** Пацан: уровень, статы, класс. Выбор класса необратим и об этом сказано. */
export function CharacterPanel({ save, onClose }: Props): JSX.Element {
  const { doer } = character(save);
  const progress = xpProgress(save.player.xp);
  const klass = findClass(save.player.classId);
  const canChooseClass = !klass && save.player.level >= CLASS_LEVEL;
  const canChooseSubclass = klass && !save.player.subclassId && save.player.level >= SUBCLASS_LEVEL;

  return (
    <div className="shop">
      <div className="deeds-head">
        <b>Пацан</b>
        <button type="button" className="deeds-close" onClick={onClose} aria-label="Закрыть">
          ✕
        </button>
      </div>

      <div className="char-level">
        <span>
          Уровень {progress.level} · {klass ? klass.title : 'без класса'}
        </span>
        <div className="daybar">
          <i style={{ width: `${(progress.into / progress.need) * 100}%` }} />
        </div>
        <span className="shop-item-meta">
          {progress.into} / {progress.need} опыта
        </span>
      </div>

      <div className="char-stats">
        {STATS.map((stat) => {
          const base = save.player[stat.key];
          const withGear = doer[stat.key];
          return (
            <div key={stat.key} className="char-stat">
              <span>{stat.title}</span>
              <b>
                {withGear}
                {withGear !== base && <i className="char-bonus"> ({base} + шмот)</i>}
              </b>
              <button
                type="button"
                disabled={save.player.statPoints <= 0}
                onClick={() => store.dispatch({ type: 'spendStatPoint', stat: stat.key })}
              >
                +
              </button>
            </div>
          );
        })}
        <p className="shop-item-meta">
          {save.player.statPoints > 0
            ? `Есть ${save.player.statPoints} очк. — распредели`
            : 'Очки статов дают за уровень'}
        </p>
      </div>

      {canChooseClass && (
        <div className="char-classes">
          <span className="hud-caption">Выбрать класс — навсегда</span>
          {allClasses().map((option) => (
            <button
              key={option.id}
              type="button"
              className="deed"
              onClick={() => store.dispatch({ type: 'chooseClass', classId: option.id, title: option.title })}
            >
              <span className="deed-title">{option.title}</span>
              <span className="deed-note">{option.description}</span>
            </button>
          ))}
        </div>
      )}

      {canChooseSubclass && (
        <div className="char-classes">
          <span className="hud-caption">Подкласс — тоже навсегда</span>
          {klass.subclasses.map((option) => (
            <button
              key={option.id}
              type="button"
              className="deed"
              onClick={() => store.dispatch({ type: 'chooseSubclass', subclassId: option.id, title: option.title })}
            >
              <span className="deed-title">{option.title}</span>
              <span className="deed-note">{option.description}</span>
            </button>
          ))}
        </div>
      )}

      {!klass && save.player.level < CLASS_LEVEL && (
        <p className="shop-item-meta">Класс выбирается на {CLASS_LEVEL}-м уровне.</p>
      )}
    </div>
  );
}
